/**
 * Types for the MARS Live panel.
 *
 * Shapes match the JSON envelopes returned by:
 *   - GET /api/mars/live/fan/vintages
 *   - GET /api/mars/live/fan/vintages/<date>/multi
 *   - GET /api/mars/live/expected-returns
 */

import type { AssetClass } from '@/lib/plotly/colors';

export type VariableName =
  | 'GDP'
  | 'Inflation'
  | 'T-Bill'
  | 'Dividend Yield'
  | '10 Year'
  | 'BAA Spread';

export type DataType = 'gdp' | 'inflation' | 'tbill' | 'dividend_yield' | 'ten_year' | 'baa_spread';

export interface FanRecord {
  obs_date: string;
  p5: number | null;
  p16: number | null;
  p50: number | null;
  p84: number | null;
  p95: number | null;
  actual: number | null;
  is_actual?: boolean;
  previous_month?: number | null;
  p25?: number | null;
  p75?: number | null;
}

export interface VintageMultiResponse {
  success: true;
  vintage_date: string;
  data: Record<VariableName, FanRecord[]>;
}

export interface VintagesListResponse {
  success: true;
  vintages: string[];
  count: number;
}

export type YRange = { min: number; max: number };

export type UiAsset = 'Equities' | 'Bonds' | 'Commodities' | 'Inflation';

/** UI asset label shown on MARS Live charts (e.g. Bonds → "Fixed Income"). */
export const ASSET_DISPLAY: Record<UiAsset, string> = {
  Equities: 'Equities',
  Bonds: 'Fixed Income',
  Commodities: 'Commodities',
  Inflation: 'Inflation Swaps',
};

/** Inverse of ASSET_DISPLAY: display label → canonical AssetClass (colour key). */
export const DISPLAY_TO_ASSET: Record<string, AssetClass> = {
  Equities: 'Equities',
  'Fixed Income': 'Bonds',
  Commodities: 'Commodities',
  'Inflation Swaps': 'Inflation',
};

export type HorizonMonths = 3 | 12 | 36;

export interface ErPoint {
  vintage_date: string;
  value: number;
}

export interface ErResponse {
  success: boolean;
  data: ErPoint[];
}

export interface ProcessedFanData {
  dates: string[];
  p5: Array<number | null>;
  p16: Array<number | null>;
  p50: Array<number | null>;
  p84: Array<number | null>;
  p95: Array<number | null>;
  actual: Array<number | null>;
  previous_month: Array<number | null>;
  /**
   * Inflation-only extras (see `processInflationRows`). Macro charts leave
   * these `undefined`; only the inflation variant of `buildFanTraces` reads
   * them.
   */
  oneMonth?: Array<number | null>;
  cb?: Array<number | null>;
}

/** Static chart definitions: one fan chart per macro variable. */
export interface FanChartDef {
  variable: VariableName;
  dataType: DataType;
  title: string;
  yFormat: string;
  colorScheme: readonly [string, string, string];
}

/** Shape of the exposures endpoint response. */
export interface ExposuresResponse {
  success: boolean;
  data: {
    dates: string[];
    series: Record<string, Array<number | null>>;
    steady_state_strategic?: Record<string, number>;
  } | null;
}

/** Shape of the volatility sub-object from the timeseries endpoint. */
export interface VolatilityResponse {
  dates: string[];
  series: Record<string, Array<number | null>>;
  names?: string[];
}
